import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Home',         to: '/' },
  { label: 'Packages',     to: '/tours' },
  { label: 'Destinations', to: '/destinations' },
  { label: 'Houseboats',   to: '/houseboats' },
  { label: 'Activities',   to: '/activities' },
  { label: 'About',        to: '/about' },
  { label: 'Contact',      to: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const solidBg   = 'rgba(255,255,255,0.97)';
  const transBg   = isHome ? 'transparent' : 'rgba(16,37,66,0.88)';
  const navBg     = scrolled ? solidBg : transBg;
  const blur      = scrolled ? 'blur(20px)' : (isHome ? 'none' : 'blur(20px)');
  const border    = scrolled ? '1px solid rgba(229,231,235,0.8)' : '1px solid rgba(255,255,255,0.10)';
  const boxShadow = scrolled ? '0 4px 24px rgba(15,76,129,0.08)' : 'none';
  const linkColor = scrolled ? 'var(--heading)' : '#fff';
  const logoColor = scrolled ? 'var(--primary)' : '#fff';

  return (
    <>
      <nav style={{
        position:'fixed', top:0, left:0, right:0, zIndex:900,
        transition:'all 0.35s cubic-bezier(0.4,0,0.2,1)',
        background: navBg,
        backdropFilter: blur,
        WebkitBackdropFilter: blur,
        borderBottom: border,
        boxShadow: boxShadow,
      }}>
        <div className="container" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', height:72 }}>

          {/* Logo */}
          <Link to="/" style={{ display:'flex', alignItems:'center', gap:'0.65rem', flexShrink:0 }}>
            <div style={{
              width:42, height:42, borderRadius:11,
              background:'linear-gradient(135deg,#0F4C81,#3AAFA9)',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:'1.15rem', color:'#fff', fontWeight:700,
              boxShadow:'0 4px 14px rgba(15,76,129,0.35)', flexShrink:0,
            }}>✦</div>
            <div style={{ lineHeight:1.15 }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:'1.05rem', color:logoColor, transition:'color .3s' }}>
                Query Tours and
              </div>
              <div style={{ fontFamily:"'Poppins',sans-serif", fontSize:'0.62rem', fontWeight:700, letterSpacing:'0.15em', textTransform:'uppercase', color: scrolled ? 'var(--secondary)' : 'rgba(255,255,255,0.70)', transition:'color .3s' }}>
                Travels
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <ul style={{ display:'flex', alignItems:'center', gap:'0.1rem', margin:0 }} className="nav-desktop">
            {NAV_LINKS.map(({ label, to }) => {
              const active = pathname === to || (to !== '/' && pathname.startsWith(to));
              return (
                <li key={to} style={{ listStyle:'none' }}>
                  <Link to={to} style={{
                    fontFamily:"'Poppins',sans-serif", fontSize:'0.84rem', fontWeight:600,
                    padding:'0.5rem 0.8rem', borderRadius:8,
                    color: active ? (scrolled ? 'var(--primary)' : 'var(--accent)') : linkColor,
                    background: active ? (scrolled ? 'rgba(15,76,129,0.08)' : 'rgba(255,255,255,0.14)') : 'transparent',
                    display:'block', transition:'all 0.18s',
                  }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.background = scrolled ? 'rgba(15,76,129,0.06)' : 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = scrolled ? 'var(--primary)' : '#fff'; } }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = linkColor; } }}
                  >
                    {label}
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Desktop CTA */}
          <div style={{ display:'flex', alignItems:'center', gap:'0.85rem' }} className="nav-desktop">
            <a href="tel:+917006478276" style={{ fontFamily:"'Poppins',sans-serif", fontSize:'0.82rem', fontWeight:600, color: scrolled ? 'var(--body)' : 'rgba(255,255,255,0.85)', display:'flex', alignItems:'center', gap:'0.35rem', whiteSpace:'nowrap' }}>
              📞 +91 7006478276
            </a>
            <Link to="/contact" className="btn btn-accent btn-sm">✦ Book Now</Link>
          </div>

          {/* Hamburger */}
          <button onClick={() => setMenuOpen(o => !o)} aria-label="Menu" className="nav-hamburger"
            style={{ width:40, height:40, borderRadius:8, display:'none', alignItems:'center', justifyContent:'center', background: scrolled ? 'rgba(15,76,129,0.08)' : 'rgba(255,255,255,0.15)', color:linkColor, border:'none', cursor:'pointer', fontSize:'1.3rem', transition:'all 0.2s' }}>
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      <div onClick={() => setMenuOpen(false)} style={{ position:'fixed', inset:0, zIndex:800, background:'rgba(16,37,66,0.50)', backdropFilter:'blur(4px)', WebkitBackdropFilter:'blur(4px)', opacity:menuOpen ? 1:0, pointerEvents:menuOpen ? 'auto':'none', transition:'opacity 0.3s' }} />

      {/* Mobile drawer */}
      <div style={{ position:'fixed', top:0, right:0, bottom:0, width:290, background:'#fff', zIndex:850, transform:menuOpen ? 'translateX(0)' : 'translateX(100%)', transition:'transform 0.35s cubic-bezier(0.4,0,0.2,1)', overflowY:'auto', display:'flex', flexDirection:'column', boxShadow:'-8px 0 40px rgba(15,76,129,0.15)' }}>
        <div style={{ padding:'1.25rem 1.5rem', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:'1rem', color:'var(--primary)' }}>Query Tours and Travels</div>
          <button onClick={() => setMenuOpen(false)} style={{ width:34, height:34, borderRadius:8, background:'var(--bg)', border:'none', cursor:'pointer', fontSize:'1rem', color:'var(--body)' }}>✕</button>
        </div>
        <nav style={{ flex:1, padding:'0.5rem 0' }}>
          {NAV_LINKS.map(({ label, to }) => {
            const active = pathname === to || (to !== '/' && pathname.startsWith(to));
            return (
              <Link key={to} to={to} onClick={() => setMenuOpen(false)} style={{ display:'block', padding:'0.9rem 1.5rem', fontFamily:"'Poppins',sans-serif", fontWeight:600, fontSize:'0.92rem', color: active ? 'var(--primary)' : 'var(--heading)', background: active ? 'rgba(15,76,129,0.06)' : 'transparent', borderLeft: active ? '3px solid var(--primary)' : '3px solid transparent', transition:'all 0.18s' }}>
                {label}
              </Link>
            );
          })}
        </nav>
        <div style={{ padding:'1.25rem 1.5rem 2.5rem', borderTop:'1px solid var(--border)', display:'flex', flexDirection:'column', gap:'0.75rem' }}>
          <a href="https://wa.me/917006478276" target="_blank" rel="noopener noreferrer" style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', padding:'0.85rem', borderRadius:12, background:'#25D366', color:'#fff', fontWeight:700, fontFamily:"'Poppins',sans-serif", fontSize:'0.88rem' }}>
            💬 WhatsApp Us
          </a>
          <Link to="/contact" style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'0.85rem', borderRadius:12, background:'linear-gradient(135deg,#0F4C81,#1a69b0)', color:'#fff', fontWeight:700, fontFamily:"'Poppins',sans-serif", fontSize:'0.88rem' }}>
            ✦ Book Now
          </Link>
        </div>
      </div>

      {/* Spacer for non-home pages */}
      {!isHome && <div style={{ height:72 }} />}

      <style>{`
        @media (max-width:960px) {
          .nav-desktop { display:none !important; }
          .nav-hamburger { display:flex !important; }
        }
      `}</style>
    </>
  );
}
